import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { PKGNeighbourhoodPanel } from '../components/pkg/PKGNeighbourhoodPanel';

interface Decision {
  id: string;
  externalId: string | null;
  title: string;
  description: string | null;
  rationale: string | null;
  alternativesConsidered: string | null;
  consequences: string | null;
  status: string;
  decisionType: string;
  impactLevel: string;
  phaseId: string | null;
  createdByAi: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * PKGV.A2 (#793) — the pkg_nodes.id anchoring this decision, resolved
   * server-side via a join in getDecisionsByProject (#1783, merged
   * e12d6d03). `null` when no PKG node exists yet for this decision.
   */
  pkgNodeId: string | null;
}

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  ACCEPTED:   { bg: 'rgba(16,185,129,0.12)',  color: 'var(--success)' },
  PROPOSED:   { bg: 'rgba(245,158,11,0.12)',  color: 'var(--warning)' },
  DEPRECATED: { bg: 'rgba(239,68,68,0.12)',   color: 'var(--error)' },
  SUPERSEDED: { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
};

const IMPACT_STYLE: Record<string, { bg: string; color: string }> = {
  CRITICAL: { bg: 'rgba(239,68,68,0.12)',    color: 'var(--error)' },
  HIGH:     { bg: 'rgba(245,158,11,0.12)',   color: 'var(--warning)' },
  MEDIUM:   { bg: 'rgba(99,102,241,0.12)',   color: 'var(--primary)' },
  LOW:      { bg: 'rgba(120,113,108,0.12)',  color: 'var(--text-secondary)' },
};

function Badge({ text, style }: { text: string; style: { bg: string; color: string } }) {
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: style.bg, color: style.color }}
    >
      {text}
    </span>
  );
}

export default function DecisionsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [decisions, setDecisions]     = useState<Decision[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [bridging, setBridging]       = useState(false);
  const [bridgeMsg, setBridgeMsg]     = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiFetch<Decision[]>(`/projects/${projectId}/decisions`)
      .then(d => { setDecisions(d); setLoading(false); })
      .catch(e => { setError((e as Error).message); setLoading(false); });
  }, [projectId]);

  async function handleBridge() {
    if (!projectId) return;
    setBridging(true);
    setBridgeMsg(null);
    try {
      const r = await apiFetch<{ decisionsCreated: number; decisionsUpdated: number; errors: string[] }>(
        `/projects/${projectId}/bridge-adrs`,
        { method: 'POST' },
      );
      setBridgeMsg(`✅ ${r.decisionsCreated} created, ${r.decisionsUpdated} updated`);
      // Refresh list
      const updated = await apiFetch<Decision[]>(`/projects/${projectId}/decisions`);
      setDecisions(updated);
    } catch (e) {
      setBridgeMsg(`Error: ${(e as Error).message}`);
    } finally {
      setBridging(false);
    }
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
  };

  return (
    <main style={{ flex: 1, padding: 'var(--space-8)', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4 }}>
              <Link
                to={`/projects/${projectId}`}
                style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
                onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
              >
                ← Dashboard
              </Link>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Decisions
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              Architecture Decision Records bridged from ADR artefacts
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            {bridgeMsg && (
              <span style={{ fontSize: 13, color: bridgeMsg.startsWith('✅') ? 'var(--success)' : 'var(--error)' }}>
                {bridgeMsg}
              </span>
            )}
            <button
              onClick={handleBridge}
              disabled={bridging}
              style={{
                padding: '8px 16px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 13,
                fontWeight: 500,
                cursor: bridging ? 'not-allowed' : 'pointer',
                opacity: bridging ? 0.7 : 1,
              }}
            >
              {bridging ? 'Syncing…' : '⚡ Sync from ADRs'}
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Loading…</p>
        ) : error ? (
          <p style={{ color: 'var(--error)', fontFamily: 'var(--font-body)' }}>{error}</p>
        ) : decisions.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
              No decisions yet. Click <strong>⚡ Sync from ADRs</strong> to bridge your ADR artefact.
            </p>
          </div>
        ) : (
          <div style={cardStyle}>
            {/* Stats row */}
            <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-5)', paddingBottom: 'var(--space-4)', borderBottom: '1px solid var(--border)' }}>
              {(['ACCEPTED', 'PROPOSED', 'DEPRECATED', 'SUPERSEDED'] as const).map(s => {
                const n = decisions.filter(d => d.status === s).length;
                if (n === 0) return null;
                return (
                  <div key={s}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 700, color: STATUS_STYLE[s]?.color ?? 'var(--text)' }}>{n}</span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginLeft: 6 }}>{s.toLowerCase()}</span>
                  </div>
                );
              })}
            </div>

            {/* Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['ID', 'Title', 'Status', 'Impact', 'Type', 'Phase'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {decisions.map(d => (
                  <>
                    <tr
                      key={d.id}
                      onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {d.externalId ?? '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500, color: 'var(--text)', maxWidth: 360 }}>
                        {d.title.replace(/^ADR-\d+:\s*/, '')}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <Badge text={d.status} style={STATUS_STYLE[d.status] ?? STATUS_STYLE['PROPOSED']} />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <Badge text={d.impactLevel} style={IMPACT_STYLE[d.impactLevel] ?? IMPACT_STYLE['MEDIUM']} />
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {d.decisionType}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {d.phaseId ?? '—'}
                      </td>
                    </tr>
                    {expandedId === d.id && (
                      <tr key={`${d.id}-detail`} style={{ background: 'var(--surface-raised, var(--surface))' }}>
                        <td colSpan={6} style={{ padding: '16px 24px 20px', borderBottom: '2px solid var(--border)' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
                            {[
                              { label: 'Context', text: d.description },
                              { label: 'Decision', text: d.rationale },
                              { label: 'Alternatives Considered', text: d.alternativesConsidered },
                              { label: 'Consequences', text: d.consequences },
                            ].map(({ label, text }) => text ? (
                              <div key={label}>
                                <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                  {label}
                                </p>
                                <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                                  {text}
                                </p>
                              </div>
                            ) : null)}
                          </div>
                          {projectId && (
                            <PKGNeighbourhoodPanel projectId={projectId} anchorNodeId={d.pkgNodeId} />
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </main>
  );
}
